import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Column } from 'devextreme-react/data-grid';

import * as Actions from '../../../redux/categoryActions';
import DataGrid from '../../../../../components/DataGrid';
import * as Selectors from '../../../redux/categorySelectors';
import useMainProgressContext from '../../../../../components/progress/MainProgress/context/MainProgressContext';
import useDataGridProgressContext from '../../../../../components/progress/DataGridProgress/context/DataGridProgressContext';
import { API_REQUEST_STATE } from '../../../../../core/enums/apiRequestState';
import { ROUTES } from '../../../../../core/router/routes';
import { DataGridCellRender } from '../../../../../components/DataGrid/model/dataGridCellRenderModel';
import { CategoryGetResponse } from '../../../model/categoryModel';
import DataGridLinkTemplate from '../../../../../components/DataGrid/template/DataGridLinkTemplate';
import DataGridObjectTemplate from '../../../../../components/DataGrid/template/DataGridObjectTemplate';
import DataGridArrayObjectsTemplate from '../../../../../components/DataGrid/template/DataGridArrayObjectsTemplate';
import { CATEGORY_ID } from '../../../enums/categoryId';
import ActionConfirmationDialog from '../../../../../components/ConfirmationDialog';
import DataGridActionTemplate from '../../../../../components/DataGrid/template/DataGridActionTemplate';
import SnackbarSettings from '../../../../../components/Snackbar/SnackbarSettings';

const CategoryDataGrid: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mainProgressContext = useMainProgressContext();
  const dataGridProgressContext = useDataGridProgressContext();
  const categories = useSelector(Selectors.categories);
  const categoriesState = useSelector(Selectors.apiCategoriesRequestState);
  const apiRequestState = useSelector(Selectors.apiCategoryRequestState);
  const apiRequestError = useSelector(Selectors.apiCategoryRequestError);
  const [open, setOpen] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const linkRender = (cellData: DataGridCellRender<CategoryGetResponse>, name: string) => {
    return <DataGridLinkTemplate to={ROUTES.CATEGORY.EDIT} value={name} id={cellData.data.id!} />;
  };
  const handleActionClick = (id: string) => {
    setCategoryId(id);
    setOpen(true);
  };

  const handleDeleteConfirm = useCallback(() => {
    dataGridProgressContext.show();
    dispatch(Actions.deleteCategory({ id: categoryId }));
    setOpen(false);
  }, [categoryId]);

  const handleDialogClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    dataGridProgressContext.show();
  }, []);

  useEffect(() => {
    dispatch(Actions.getAllCategories({}));
  }, []);

  useEffect(() => {
    mainProgressContext.hide();
  }, [categories]);

  useEffect(() => {
    if (categoriesState === (API_REQUEST_STATE.SUCCEEDED_GET || API_REQUEST_STATE.SUCCEEDED_DELETE)) {
      setTimeout(() => {
        dataGridProgressContext.hide();
      }, 500);
    }
  }, [categoriesState]);

  useEffect(
    () => () => {
      dispatch(Actions.resetCategory());
    },
    [],
  );

  return (
    <>
      <DataGrid rows={categories}>
        <Column
          dataField="id"
          caption={t('category.id')}
          width="10%"
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<CategoryGetResponse>) => {
            return cellData.data.id !== CATEGORY_ID.ROOT ? linkRender(cellData, cellData.data.id!) : cellData.data.id;
          }}
        />
        <Column
          dataField="name"
          caption={t('category.name')}
          width="30%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<CategoryGetResponse>) => {
            return cellData.data.id !== CATEGORY_ID.ROOT
              ? linkRender(cellData, cellData.data.name)
              : cellData.data.name;
          }}
        />
        <Column
          dataField="levelNo"
          caption={t('category.level_categories')}
          width="15%"
          allowSorting={false}
          minWidth="150"
          alignment={'left'}
        />
        <Column
          dataField="parent"
          caption={t('category.parent')}
          width="30%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<CategoryGetResponse>) => {
            return <DataGridObjectTemplate value={cellData.data.parent!} />;
          }}
        />
        <Column
          dataField="subcategories"
          caption={t('category.subcategories')}
          width="30%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<CategoryGetResponse>) => {
            return <DataGridArrayObjectsTemplate value={cellData.data.subcategories} />;
          }}
        />
        <Column
          dataField="actions"
          caption=""
          width="10%"
          allowSorting={false}
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<CategoryGetResponse>) => {
            if (
              cellData.data.parent !== null &&
              cellData.data.parent.id !== 1 &&
              cellData.data.subcategories.length === 0
            ) {
              return <DataGridActionTemplate onClick={() => handleActionClick(cellData.data.id!)} />;
            }
          }}
        />
      </DataGrid>
      <ActionConfirmationDialog
        open={open}
        onConfirm={handleDeleteConfirm}
        onClose={handleDialogClose}
        title={'category.confirmation.confirmation'}
        text={'category.confirmation.delete'}
      />

      <SnackbarSettings
        successMessage="category.snackbar.delete_success"
        apiRequestState={apiRequestState}
        apiRequestError={apiRequestError}
        setApiRequestState={Actions.setApiCategoryRequestState}
        onSuccess={() => {
          dispatch(Actions.getAllCategories({}));
        }}
        onError={() => {
          dataGridProgressContext.hide();
        }}
      />
    </>
  );
};

export default CategoryDataGrid;
